/**
 * Pool de mots-clés SEO/AEO/GEO — FR + AR (darija/MENA).
 * Utilisé par <Seo keywords={...} /> et injecté dans knowsAbout (JSON-LD).
 * Google ignore <meta keywords>, mais Bing/Yandex + certains LLM crawlers
 * les lisent encore, et ça sert de source pour la génération de contenu.
 */

export const KEYWORDS_COMMUNICATION = [
  "agence de communication",
  "agence de communication Maroc",
  "agence communication Casablanca",
  "stratégie de communication",
  "campagne publicitaire",
  "publicité digitale",
  "branding",
  "identité de marque",
  "positionnement de marque",
  "marketing de contenu",
  "plan média",
  "communication visuelle",
  "communication corporate",
  "communication institutionnelle",
  "direction artistique",
  "storytelling de marque",
  "publicité créative",
  "agence communication digitale",
];

export const KEYWORDS_DESIGN = [
  "design graphique",
  "conception graphique",
  "graphiste freelance",
  "logo professionnel",
  "identité visuelle",
  "charte graphique",
  "packaging design",
  "illustration vectorielle",
  "maquette graphique",
  "création visuelle",
  "design print",
  "design digital",
  "visuels réseaux sociaux",
  "bannière publicitaire",
  "affiche publicitaire",
  "flyer design",
  "brochure design",
  "création d'infographie",
  "studio graphique",
  "refonte de logo",
];

export const KEYWORDS_FABRICATION = [
  "fichier prêt à imprimer",
  "fichier de fabrication",
  "dossier de fabrication",
  "fichier vectoriel",
  "fichier source",
  "fichier haute définition",
  "fichier 300 dpi",
  "fichier CMJN",
  "fichier PDF print",
  "fichier PDF vectorisé",
  "fichier AI",
  "fichier EPS",
  "fichier SVG",
  "fichier PSD",
  "fichier InDesign",
  "gabarit de fabrication",
  "fichier pour découpe",
  "fichier pour sérigraphie",
  "fichier pour signalétique",
  "fichier pour packaging",
];

export const KEYWORDS_COMMERCIAL = [
  "devis communication",
  "tarif design graphique",
  "prix logo professionnel",
  "agence communication prix",
  "agence branding premium",
  "design graphique professionnel",
  "communication sur mesure",
  "impression urgente",
];

export const KEYWORDS_GEO_MENA = [
  "agence communication Casablanca",
  "design graphique Marrakech",
  "agence communication Algérie",
  "graphiste Tunis",
  "agence branding Dubai",
  "design graphique Riyad",
  "impression Le Caire",
  "agence communication Doha",
  "packaging design Koweït",
  "communication digitale Libye",
];

export const KEYWORDS_ARABIC = [
  "وكالة إعلانات",
  "إستراتيجية اتصالات",
  "حملة إعلانية",
  "إعلان رقمي",
  "هوية بصرية",
  "تصميم جرافيك",
  "مصمم جرافيك",
  "تصميم شعار",
  "تصميم تغليف",
  "تصميم مطبوعات",
  "ملفات تصميم",
  "ملف فيكتور",
  "ملف طباعة",
  "ملف ديزاين",
  "ملف جاهز للطباعة",
];

/** Pool complet, dédupliqué. */
export const KEYWORDS_ALL = Array.from(
  new Set([
    ...KEYWORDS_COMMUNICATION,
    ...KEYWORDS_DESIGN,
    ...KEYWORDS_FABRICATION,
    ...KEYWORDS_COMMERCIAL,
    ...KEYWORDS_GEO_MENA,
    ...KEYWORDS_ARABIC,
  ]),
);

/** Top mots-clés à mettre dans <meta keywords> de la home. */
export const KEYWORDS_HOME = [
  ...KEYWORDS_COMMUNICATION.slice(0, 8),
  ...KEYWORDS_DESIGN.slice(0, 8),
  ...KEYWORDS_FABRICATION.slice(0, 6),
  ...KEYWORDS_COMMERCIAL.slice(0, 4),
  ...KEYWORDS_GEO_MENA.slice(0, 6),
  ...KEYWORDS_ARABIC.slice(0, 8),
];

/** Pool de mots-clés spécifique à chaque univers (service pilier). */
export const KEYWORDS_BY_SERVICE: Record<string, string[]> = {
  industripub: [
    "enseigne lumineuse", "enseigne LED", "enseigne façade", "lettres découpées",
    "lettres boîtiers", "dossier enseigne Maroc", "fichier CNC prêt à découper",
    "signalétique commerciale", "habillage vitrine", "totem publicitaire",
    "لافطة", "لافطة مضيئة", "ملف CNC", "دوسي لافطة",
    ...KEYWORDS_FABRICATION.slice(0, 8),
  ],
  digitalpub: [
    "publicité Facebook Maroc", "publicité Instagram", "campagne Meta Ads",
    "publicité TikTok", "community management Casablanca", "gestion réseaux sociaux",
    "contenu réseaux sociaux", "vidéo publicitaire", "motion design",
    "إعلان فيسبوك", "إعلان انستغرام", "تيك توك ماركتينغ",
    ...KEYWORDS_COMMUNICATION.slice(0, 6),
  ],
  printpub: [
    "impression numérique Maroc", "impression offset", "impression grand format",
    "flyer Casablanca", "brochure entreprise", "carte de visite premium",
    "roll-up impression", "kakémono", "bâche publicitaire", "packaging carton",
    "طباعة", "طباعة رقمية", "فليير", "كارت زيارة", "بروشور",
    ...KEYWORDS_DESIGN.slice(0, 6),
  ],
  "3design": [
    "modélisation 3D", "rendu 3D photoréaliste", "visualisation architecturale",
    "mockup 3D produit", "prototype 3D", "animation 3D produit",
    "présentation 3D packaging", "visite virtuelle 3D",
    "تصميم ثلاثي الأبعاد", "رندر 3D", "موكاب 3D",
  ],
  dev: [
    "création site web Maroc", "développement site vitrine", "site e-commerce Casablanca",
    "application web sur mesure", "landing page conversion", "site WordPress professionnel",
    "développement React", "site rapide SEO", "PWA Maroc", "intégration Shopify",
    "تصميم موقع", "موقع إلكتروني", "متجر إلكتروني", "تطوير ويب",
  ],
};

/**
 * Construit un pool de meta keywords unique par page.
 * - `seeds` : mots-clés spécifiques à la page (titre, slug, ville…), placés en tête.
 * - `pools` : pools thématiques (KEYWORDS_BY_SERVICE[slug], KEYWORDS_ARABIC…).
 * Résultat dédupliqué, borné à `max` entrées pour rester lisible côté crawler.
 */
export const buildPageKeywords = (
  seeds: string[],
  pools: string[][] = [],
  max = 40,
): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (k: string) => {
    const clean = k?.trim();
    if (!clean) return;
    const key = clean.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(clean);
  };
  seeds.forEach(push);
  pools.forEach((p) => p.forEach(push));
  return out.slice(0, max);
};
